// eslint-disable-next-line fsd/layer-imports
import { useProfile } from '@/entities/profile'

import { useAmplitude } from './useAmplitude'

interface SessionData {
  sessionId: string
  loginTime: number
  lastActivityTime: number
  isActive: boolean
}

export const useActivityTracking = () => {
  const { profile } = storeToRefs(useProfile())
  const { track } = useAmplitude()

  const SESSION_TIMEOUT = 30 * 60 * 1000
  const ACTIVITY_CHECK_INTERVAL = 60 * 1000
  const STORAGE_KEY = 'user_session_data'

  const generateSessionId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  }

  const getSessionData = (): SessionData | null => {
    try {
      const data = localStorage.getItem(STORAGE_KEY)
      return data ? JSON.parse(data) : null
    } catch {
      return null
    }
  }

  const saveSessionData = (sessionData: SessionData) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData))
  }

  const isSessionExpired = (lastActivityTime: number): boolean => {
    return Date.now() - lastActivityTime > SESSION_TIMEOUT
  }

  const startNewSession = () => {
    if (!profile.value) {
      return null
    }

    const now = Date.now()
    const sessionData: SessionData = {
      sessionId: generateSessionId(),
      loginTime: now,
      lastActivityTime: now,
      isActive: true,
    }

    saveSessionData(sessionData)

    track('user_login', {
      user_id: profile.value.id,
      session_id: sessionData.sessionId,
      timestamp: now,
      login_method: 'oidc',
      user_role: profile.value.role,
      department: profile.value.department?.name || 'unassigned',
    })

    return sessionData
  }

  const updateActivity = (activityType = 'general') => {
    if (!profile.value) {
      return
    }

    const now = Date.now()
    let sessionData = getSessionData()

    if (!sessionData || isSessionExpired(sessionData.lastActivityTime)) {
      if (sessionData && isSessionExpired(sessionData.lastActivityTime)) {
        track('session_timeout', {
          user_id: profile.value.id,
          session_id: sessionData.sessionId,
          timestamp: now,
          session_duration: sessionData.lastActivityTime - sessionData.loginTime,
          timeout_minutes: 30,
        })
      }

      sessionData = startNewSession()
      if (!sessionData) {
        return
      }
    }

    sessionData.lastActivityTime = now
    sessionData.isActive = true
    saveSessionData(sessionData)

    track('last_activity', {
      user_id: profile.value.id,
      session_id: sessionData.sessionId,
      timestamp: now,
      activity_type: activityType,
      session_duration: now - sessionData.loginTime,
    })
  }

  const endSession = (reason = 'manual') => {
    const sessionData = getSessionData()
    if (!sessionData || !profile.value) {
      return
    }

    const now = Date.now()
    const sessionDuration = sessionData.lastActivityTime - sessionData.loginTime

    track('session_end', {
      user_id: profile.value.id,
      session_id: sessionData.sessionId,
      timestamp: now,
      session_duration: sessionDuration,
      end_reason: reason,
    })

    localStorage.removeItem(STORAGE_KEY)
  }

  const getCurrentSessionDuration = (): number => {
    const sessionData = getSessionData()
    if (!sessionData) {
      return 0
    }

    return sessionData.lastActivityTime - sessionData.loginTime
  }

  const initActivityTracking = () => {
    const sessionData = getSessionData()
    if (!sessionData || isSessionExpired(sessionData.lastActivityTime)) {
      startNewSession()
    }

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'focus']

    const throttledUpdateActivity = throttle(() => {
      updateActivity('user_interaction')
    }, 30000)

    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity, true)
    })

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        updateActivity('tab_focus')
      } else {
        updateActivity('tab_blur')
      }
    })

    const activityChecker = setInterval(() => {
      const currentSessionData = getSessionData()
      if (currentSessionData && isSessionExpired(currentSessionData.lastActivityTime)) {
        endSession('timeout')
        clearInterval(activityChecker)
      }
    }, ACTIVITY_CHECK_INTERVAL)

    // Return cleanup function
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledUpdateActivity, true)
      })
      clearInterval(activityChecker)
    }
  }

  function throttle<T extends (...args: unknown[]) => void>(func: T, wait: number) {
    let timeout: NodeJS.Timeout | null = null
    let previous = 0

    return function (this: unknown, ...args: Parameters<T>) {
      const now = Date.now()
      const remaining = wait - (now - previous)

      if (remaining <= 0 || remaining > wait) {
        if (timeout) {
          clearTimeout(timeout)
          timeout = null
        }
        previous = now
        func.apply(this, args)
      } else if (!timeout) {
        timeout = setTimeout(() => {
          previous = Date.now()
          timeout = null
          func.apply(this, args)
        }, remaining)
      }
    }
  }

  return {
    initActivityTracking,
    updateActivity,
    endSession,
    getCurrentSessionDuration,
    getSessionData,
    SESSION_TIMEOUT,
  }
}
